/**
 * Footer cloned from the marketing site (ClickUp 86banyvn7) — same columns,
 * links, and styling as apps/marketing/src/components/footer.tsx, with the
 * internal marketing routes rewritten as absolute cross-origin URLs. Keep
 * the two files in sync.
 */
import { Linkedin, Instagram, Youtube, Facebook } from 'lucide-react';
import { MARKETING_SITE_URL } from '@/lib/funnel';

const quickLinks = [
  { href: '/products', label: 'Products' },
  { href: '/about', label: 'About' },
  { href: '/blog', label: 'Blog' },
  { href: '/contact', label: 'Contact' },
  { href: '/support', label: 'Support' },
  { href: '/careers', label: 'Careers' },
];

const legalLinks = [
  { href: '/privacy', label: 'Privacy Policy' },
  { href: '/terms', label: 'Terms of Service' },
];

const socialLinks = [
  {
    href: 'https://www.youtube.com/@LegacyOrb',
    label: 'YouTube',
    icon: Youtube,
  },
  {
    href: 'https://www.facebook.com/profile.php?id=61590442447040',
    label: 'Facebook',
    icon: Facebook,
  },
  {
    href: 'https://www.instagram.com/legacy_orb/',
    label: 'Instagram',
    icon: Instagram,
  },
  {
    href: 'https://www.linkedin.com/company/legacy-orb/',
    label: 'LinkedIn',
    icon: Linkedin,
  },
];

export function MarketingFooter() {
  return (
    <footer className="border-t bg-orb-onyx text-white">
      <div className="container py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center">
              <img
                src="/logos/Logo_right_text_Transparent.png"
                alt="LegacyOrb"
                width={480}
                height={150}
                className="h-10 w-auto invert"
              />
            </div>
            <p className="mt-3 text-sm text-white/60">
              Merging creative services with cutting-edge AI technology to preserve your personal
              life story for future generations.
            </p>
            <div className="mt-4 flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <social.icon size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold mb-4">Quick Links</h4>
            <nav className="flex flex-col gap-2">
              {quickLinks.map((link) => (
                <a
                  key={link.href}
                  href={`${MARKETING_SITE_URL}${link.href}`}
                  className="text-sm text-white/60 hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-semibold mb-4">Legal</h4>
            <nav className="flex flex-col gap-2">
              {legalLinks.map((link) => (
                <a
                  key={link.href}
                  href={`${MARKETING_SITE_URL}${link.href}`}
                  className="text-sm text-white/60 hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-sm font-semibold mb-4">Get in Touch</h4>
            <div className="flex flex-col gap-2 text-sm text-white/60">
              <a href="mailto:Info@LegacyOrb.com" className="hover:text-white transition-colors">
                Info@LegacyOrb.com
              </a>
              <a href="tel:+17036798707" className="hover:text-white transition-colors">
                (703) 679-8707
              </a>
            </div>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-white/10 text-center text-sm text-white/40">
          &copy; {new Date().getFullYear()} LegacyOrb LLC. All rights reserved. Patent pending.
        </div>
      </div>
    </footer>
  );
}
